import { Link } from "react-router-dom";
import { ArrowRight, Check, ClipboardList, Download, Landmark, QrCode } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useMeta } from "@/lib/useMeta";
import { lines, media } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";
import { Carousel } from "@/components/site/Carousel";

const Bank = ({ b }) => (
  <div className="bg-ivory-alt/70 border-l-4 border-brass rounded-r-lg px-6 py-6" data-testid="buy-bank-details">
    <h3 className="font-sans font-bold text-lg text-charcoal flex items-center gap-2 mb-4"><Landmark className="w-5 h-5 text-crimson" />{b.bank_title}</h3>
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm sm:text-[15px]">
      {lines(b.bank_lines).map((l, i) => {
        const [k, v] = l.split("|");
        return [
          <dt key={`k${i}`} className="text-stone-500">{k}</dt>,
          <dd key={`v${i}`} className="text-charcoal font-bold break-words" data-testid={`buy-bank-${i}`}>{v}</dd>,
        ];
      })}
    </dl>
  </div>
);

const Prices = ({ b }) => (
  <div className="mt-10" data-testid="buy-prices">
    <p className="font-sans font-bold text-charcoal mb-4">{b.prices_title}</p>
    <div className="grid sm:grid-cols-3 gap-3">
      {lines(b.price_lines).map((l, i, all) => {
        const [label, price, note] = l.split("|");
        const hi = i === all.length - 1;
        return (
          <div key={i} className={`rounded-lg p-5 flex flex-col border ${hi ? "bg-charcoal border-charcoal text-white" : "bg-white border-stone-200"}`} data-testid={`buy-price-${i}`}>
            <span className={`text-xs font-bold uppercase tracking-wide ${hi ? "text-brass" : "text-stone-500"}`}>{label}</span>
            <span className={`mt-2 text-2xl font-bold ${hi ? "text-white" : "text-crimson"}`}>{price}</span>
            {note?.trim() && <span className={`mt-1 text-xs leading-snug ${hi ? "text-stone-300" : "text-stone-500"}`}>({note.trim()})</span>}
          </div>
        );
      })}
    </div>
    {b.note && <p className="mt-4 flex items-center gap-2 text-sm text-stone-600" data-testid="buy-note"><Download className="w-4 h-4 text-crimson shrink-0" />{b.note}</p>}
  </div>
);

const Features = ({ items }) => (
  <ul className="mt-6 grid sm:grid-cols-2 gap-2.5" data-testid="buy-features">
    {items.map((f, i) => (
      <li key={i} className="flex gap-3 items-start bg-ivory/80 border border-stone-200/70 rounded-md px-4 py-3 text-sm text-charcoal" data-testid={`buy-feature-${i}`}>
        <span className="w-5 h-5 rounded-full bg-crimson text-white flex items-center justify-center shrink-0 mt-px"><Check className="w-3 h-3" strokeWidth={3} /></span>
        {f}
      </li>
    ))}
  </ul>
);

const Payment = ({ b }) => (
  <div className="mt-10 space-y-6">
    <Bank b={b} />
    {b.qr_image && (
      <div className="bg-white border border-stone-200 rounded-xl shadow-sm p-6 sm:p-8 flex flex-col items-center" data-testid="buy-qr">
        <h3 className="font-sans font-bold text-lg text-charcoal flex items-center gap-2 mb-5"><QrCode className="w-5 h-5 text-crimson" />{b.qr_title}</h3>
        <img src={media(b.qr_image)} alt="UPI QR code" className="w-full max-w-[380px] h-auto" data-testid="buy-qr-image" />
      </div>
    )}
  </div>
);

export default function Buy() {
  const { content } = useSite();
  useMeta("buy");
  const b = content.buy;
  return (
    <div data-testid="buy-page">
      <section className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h1 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight break-words" data-testid="buy-hero-title">
            {b.hero_title}<span className="block text-brass mt-2">{b.hero_title2}</span>
          </h1>
          <div className="mt-6 text-stone-300 text-base sm:text-lg space-y-1">
            {lines(b.hero_subtitle_lines).map((l, i) => <p key={i}>{l}</p>)}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <Reveal className="min-w-0">
            <span className="block h-[2px] w-12 bg-brass mb-5" />
            <h2 className="font-sans text-2xl sm:text-[28px] font-bold text-charcoal leading-snug" data-testid="buy-title">{b.title}</h2>
            <Rich html={b.intro_html} className="mt-5 text-stone-700 text-[15px] sm:text-base leading-relaxed" testid="buy-body" />
            <Features items={lines(b.features_lines)} />
            <Prices b={b} />
            <Rich html={b.closing_html} className="mt-10 border-l-4 border-crimson pl-5 font-serif italic text-lg sm:text-xl text-charcoal leading-relaxed" testid="buy-closing" />
            <Payment b={b} />
          </Reveal>
          <Reveal delay={0.1} className="min-w-0 lg:sticky lg:top-24">
            <div className="bg-ivory-alt rounded-xl border border-stone-200 shadow-sm overflow-hidden" data-testid="buy-form-card">
              <div className="bg-crimson text-white font-sans font-bold px-5 py-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" />{b.form_title}</div>
              <iframe src={b.form_url} title={b.form_title} width="640" height={Number(b.form_height) || 1605} frameBorder="0" marginHeight="0" marginWidth="0" className="block w-full bg-white" data-testid="buy-form-iframe">Loading…</iframe>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-16 sm:py-20" data-testid="buy-editions">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl text-charcoal text-center mb-12 px-4">{content.editions.title}</h2>
        <Carousel images={content.editions.images} testid="buy-editions-carousel" />
      </section>

      <section className="bg-charcoal border-t-4 border-crimson" data-testid="buy-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <h2 className="font-sans text-xl sm:text-2xl font-bold text-white leading-snug">{b.cta_title}</h2>
            <Rich html={b.cta_html} className="mt-4 text-stone-400 text-sm leading-relaxed [&_a]:text-brass" />
          </div>
          <Link to="/listing-questionnaire" className="justify-self-start lg:justify-self-end inline-flex items-center gap-2 border-2 border-white text-white font-bold text-sm rounded-full px-7 py-3.5 hover:bg-white hover:text-charcoal transition-colors" data-testid="buy-cta-btn">
            {b.cta_button}<ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
